import classNames from "classnames";
import HTMLReactParser from "html-react-parser";
import React from "react";
import { Breakpoint, Breakpoints, CSSNumericValue, ScreenWidthMediaQueryPreset } from "../components/types";

export const cloneIcon = (iconElement: JSX.Element, className: string) =>
  React.cloneElement(iconElement, {
    size: iconElement.props.size || 24,
    color: iconElement.props.color || "currentcolor",
    className: classNames(className, iconElement.props.className),
  });

export const safelyAddClassName = (classList: DOMTokenList, className?: string) => {
  if (classList && className && className !== "") {
    classList.add(...className.split(" "));
  }
};

export const safelyRemoveClassName = (classList: DOMTokenList, className?: string) => {
  if (classList && className && className !== "") {
    classList.remove(...className.split(" "));
  }
};

const PREFIXED_PRESET_PATTERN = /^below-(sm|md|lg|xl|2xl|3xl)$/;
const SUFFIXED_PRESET_PATTERN = /^(sm|md|lg|xl|2xl|3xl)-and-above$/;

/**
 * A utility function that returns a media query string for the `screen` media type and a `max-width` condition on the
 * passed in CSS width value. The `max-width` condition is applied to a value that is 1px less than the CSS width value,
 * thus it does not include the value itself in the `max-width` condition. So, effectively, the condition acts as an
 * *up to but not including this width value* condition.
 *
 * @param width A CSS width value
 */
export const getMaxWidthScreenMediaQueryString = (width: string) => `screen and (max-width: calc(${width} - 1px))`;

/**
 * A utility function that returns a media query string for the `screen` media type and a `min-width` condition on the
 * passed in CSS width value. The `min-width` condition is applied directly to the CSS width value. So, effectively, the
 * condition acts as an *this width value and up* condition.
 *
 * @param width A CSS width value
 */
export const getMinWidthScreenMediaQueryString = (width: string) => `screen and (min-width: ${width})`;

/**
 * A utility function that extracts and returns a breakpoint value from within a screen media query preset value.
 * It essentially ignores any prefix or suffix to narrow down to just the breakpoint value.
 * E.g.
 * For the preset `below-md` it returns `md`.
 * For the preset `lg-and-above` it returns `lg`.
 *
 * It returns `undefined` for invalid `preset` values.
 *
 * @param preset A screen media query preset value
 */
export const getBreakpointFromPreset = (preset: ScreenWidthMediaQueryPreset) => {
  if (!preset) {
    return undefined;
  }

  const prefixedPreset = preset.match(PREFIXED_PRESET_PATTERN);
  if (prefixedPreset) {
    return prefixedPreset[1] as Breakpoint;
  }

  const suffixedPreset = preset.match(SUFFIXED_PRESET_PATTERN);
  if (suffixedPreset) {
    return suffixedPreset[1] as Breakpoint;
  }
};

/**
 * A utility function that returns a breakpoint width CSS value that matches a given screen media query preset value.
 * It relies on a `breakpoints` mapping object that is expected to have CSS width values for each breakpoint.
 * It also relies on a `desktopBreakpoint` for its mapping logic.
 *
 * It returns `undefined` for invalid `preset` or `desktopBreakpoint` values.
 *
 * @param preset A screen media query preset value
 * @param breakpoints A breakpoint to CSS width value mapping object
 * @param desktopBreakpoint A breakpoint value to be used as the mobile-to-desktop threshold breakpoint
 */
export const getBreakpointValueForPreset = (
  preset: ScreenWidthMediaQueryPreset,
  breakpoints: Breakpoints = {} as Breakpoints,
  desktopBreakpoint: Breakpoint
) => {
  if (preset === "mobile" || preset === "desktop") {
    return breakpoints[desktopBreakpoint];
  } else {
    const breakpoint = getBreakpointFromPreset(preset);
    if (!breakpoint) {
      return undefined;
    }
    return breakpoints[breakpoint];
  }
};

/**
 * A utility function that returns a `CSS width value` based media query string for the `screen` media type based on a
 * `preset` value, available breakpoint values and a special mobile-to-desktop threshold breakpoint.
 *
 * @param preset A screen media query preset value
 * @param breakpoints A breakpoint to CSS width value mapping object
 * @param desktopBreakpoint A breakpoint value to be used as the mobile-to-desktop threshold breakpoint
 */
export const getScreenMediaQueryStringForPreset = (
  preset: ScreenWidthMediaQueryPreset,
  breakpoints: Breakpoints = {} as Breakpoints,
  desktopBreakpoint: Breakpoint
) => {
  if (!preset || !desktopBreakpoint) {
    return undefined;
  }

  const breakpointValue = getBreakpointValueForPreset(preset, breakpoints, desktopBreakpoint);
  if (!breakpointValue) {
    return undefined;
  }

  if (preset === "mobile" || PREFIXED_PRESET_PATTERN.test(preset)) {
    return getMaxWidthScreenMediaQueryString(breakpointValue);
  }

  if (preset === "desktop" || SUFFIXED_PRESET_PATTERN.test(preset)) {
    return getMinWidthScreenMediaQueryString(breakpointValue);
  }
};

/**
 * A
 *
 * @param preset A screen media query preset value
 * @param breakpoints A breakpoint to CSS width value mapping object
 * @param desktopBreakpoint A breakpoint value to be used as the mobile-to-desktop threshold breakpoint
 */
export const matchMediaQueryPreset = (
  preset: ScreenWidthMediaQueryPreset,
  availableBreakpoints: Breakpoints,
  desktopBreakpoint: Breakpoint
): MediaQueryList => {
  const mediaQueryString = getScreenMediaQueryStringForPreset(preset, availableBreakpoints, desktopBreakpoint);

  if (typeof matchMedia === "undefined" || !matchMedia || !mediaQueryString) {
    return undefined;
  }

  return matchMedia(mediaQueryString);
};

const toCSSNumericValueString = ({ value, unit }: CSSNumericValue = { value: 0, unit: "%" }) =>
  value === 0 ? 0 : `${value}${unit}`;

const toCSSFunctionString = (name: string = "", argument: string = "") =>
  name && argument ? `${name}(${argument})` : "";

export const toCSSTranslate3dFunctionString = (
  x: CSSNumericValue,
  y: CSSNumericValue = { value: 0, unit: "%" },
  z: CSSNumericValue = { value: 0, unit: "%" }
) => {
  const argument = [x, y, z].map(toCSSNumericValueString).join(", ");
  return toCSSFunctionString("translate3d", argument);
};

export const ParseHTML = (htmlString: string) => {
  return HTMLReactParser(htmlString);
};
